import { Extension } from '@tiptap/core'
import { ReactRenderer } from '@tiptap/react'
import Suggestion, { type SuggestionProps, type SuggestionKeyDownProps } from '@tiptap/suggestion'
import { SlashCommandList, type SlashCommandListRef } from './SlashCommandList'
import { filterSlashItems, type SlashItem } from './slashItems'

// A Notion-style "/" menu. Built on @tiptap/suggestion (the mentions engine)
// with a manually-positioned ReactRenderer popup — no tippy dependency.
export interface SlashCommandOptions {
  commandItems: SlashItem[]
}

export const SlashCommand = Extension.create<SlashCommandOptions>({
  name: 'slashCommand',

  addOptions() {
    return { commandItems: [] }
  },

  addProseMirrorPlugins() {
    const options = this.options
    return [
      Suggestion<SlashItem>({
        editor: this.editor,
        char: '/',
        allowSpaces: false,
        startOfLine: false,
        items: ({ query }) => filterSlashItems(options.commandItems, query).slice(0, 10),
        command: ({ editor, range, props }) => props.run(editor, range),
        render: renderPopup,
      }),
    ]
  },
})

function renderPopup() {
  let component: ReactRenderer<SlashCommandListRef> | null = null
  let el: HTMLDivElement | null = null

  const place = (rect: DOMRect | null | undefined) => {
    if (!el || !rect) return
    el.style.left = `${rect.left + window.scrollX}px`
    el.style.top = `${rect.bottom + window.scrollY + 6}px`
  }

  return {
    onStart: (props: SuggestionProps<SlashItem>) => {
      component = new ReactRenderer(SlashCommandList, { props, editor: props.editor })
      el = document.createElement('div')
      el.style.position = 'absolute'
      el.style.zIndex = '60'
      el.appendChild(component.element)
      document.body.appendChild(el)
      place(props.clientRect?.())
    },
    onUpdate: (props: SuggestionProps<SlashItem>) => {
      component?.updateProps(props)
      place(props.clientRect?.())
    },
    onKeyDown: (props: SuggestionKeyDownProps) => {
      if (props.event.key === 'Escape') {
        el?.remove()
        el = null
        return true
      }
      return component?.ref?.onKeyDown(props) ?? false
    },
    onExit: () => {
      el?.remove()
      el = null
      component?.destroy()
      component = null
    },
  }
}
